import { useState } from 'react';
import Heading from '@alltrails/denali/components/Heading';
import IconButton from '@alltrails/denali/components/IconButton';
import StatusBadge from '@alltrails/denali/components/StatusBadge';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Close from '@alltrails/denali/icons/Close';
import PageUIMapHoverProvider from '@alltrails/maps/components/PageUIMapHoverProvider/PageUIMapHoverProvider';
import { useSidebar } from '@alltrails/maps/components/Sidebar/useSidebar';
import useAskAccess from '@/hooks/useAskAccess';
import useAskQuestion from '@/hooks/useAskQuestion';
import useAskResponse from '@/hooks/useAskResponse';
import { useAppDispatch } from '@/utils/redux';
import { exitAsk } from '@/utils/slices/askSlice';
import AskActionProvider from './AskActionProvider';
import AskEmptyState from './AskEmptyState';
import AskInput from './AskInput';
import AskResponse from './AskResponse';
import styles from './AskSidebar.module.scss';

const AskSidebar = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();
  const { request, ...response } = useAskResponse();
  const ask = useAskQuestion();
  // TODO(XPLR-256): real analytics values
  const { modals, withAskAccess } = useAskAccess({ signUpTrigger: 'Banner on Explore' });
  const [question, setQuestion] = useState('');

  const title = 'Ask AllTrails'; // hwandrew TODO: XPLR-204 translations
  const closeLabel = 'Close Ask AllTrails'; // hwandrew TODO: XPLR-204 translations
  const betaLabel = 'Beta'; // hwandrew TODO: XPLR-204 translations

  const plusBadge = <PlusLogoBadge size="sm" variant="icon" />;
  const betaBadge = <StatusBadge emphasis="mid" size="sm" text={betaLabel} variant="neutral" />;

  const closeButton = <IconButton icon={{ Component: Close }} onClick={() => dispatch(exitAsk())} size="sm" title={closeLabel} />;

  const { SidebarHeader, SidebarContent, SidebarFooter } = useSidebar({ additionalButton: closeButton, collapseBehavior: 'none', fullHeight: true });

  const submit = (text: string) => {
    setQuestion(text);
    withAskAccess(() => {
      ask(text);
      setQuestion('');
    });
  };

  const body = request ? (
    <PageUIMapHoverProvider>
      <AskActionProvider>
        <div className={styles.transcript}>
          <AskResponse {...response} request={request} />
        </div>
      </AskActionProvider>
    </PageUIMapHoverProvider>
  ) : (
    <AskEmptyState onSuggestionClick={submit} />
  );

  const input = <AskInput onChange={setQuestion} onSubmit={submit} value={question} />;

  if (isMobile) {
    return (
      <div className={styles.mobileContainer}>
        <div className={styles.mobileHeader}>
          <div className={styles.title}>
            {plusBadge}
            <Heading as="h2" size="300">
              {title}
            </Heading>
            {betaBadge}
          </div>
          {closeButton}
        </div>
        <div className={styles.mobileBody}>{body}</div>
        <div className={styles.mobileInput}>{input}</div>
        {modals}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {SidebarHeader(
        <span className={styles.title}>
          {plusBadge}
          <span>{title}</span>
          {betaBadge}
        </span>
      )}
      {SidebarContent(body, { className: request ? undefined : styles.fillContent })}
      {SidebarFooter(input)}
      {modals}
    </div>
  );
};

export default AskSidebar;
